import { ISiderMenu } from '@contexts/AuthProvider';
import { AuthService } from '@core/services/auth.service';
import React, { ComponentType } from 'react';
import { Redirect, useLocation } from 'react-router';

const trimSlash = (path: string): string => path.replace(/\/+$/, '');

/**
 * Hides a screen the user holds no menu for.
 *
 * The routes carry no permission metadata, and withCheckAccessTime - the guard they already had - asks
 * the API whether the menu is inside its operating HOURS, not whether the caller holds it:
 * UserController.checkAccess takes a menuId and a LocalTime and never receives the caller. So it always
 * said yes for a valid menu id, and menuId is a URL parameter. Typing
 * /directory-management/?menuId=...&roleId=7 was enough to render an administrator screen.
 *
 * Cosmetic only: the API is the real gate and now refuses these calls itself. This decides whether the
 * user sees a shell full of failures or is sent home. It therefore fails OPEN whenever the menu list is
 * not yet known, so a slow or failed /users/menu can never lock out a legitimate user.
 *
 * First of two layers, and the only one that can act before anything renders. It knows only what the
 * cached sidebar knows, so it cannot see a menu revoked mid-session or a stale x-menu-id in an old tab -
 * those surface as a 403 from the API, and the axios interceptor sends the user home on those. Keep
 * both: this one avoids the flash of an empty shell, that one catches what this cannot see.
 */
export function withMenuAccess<P>(Component: ComponentType<P>): React.FC<P> {
  return function withMenuAccess(props: P): JSX.Element {
    const { pathname } = useLocation();

    const stored = AuthService.getSideMenus();
    const siderMenus: ISiderMenu | undefined = stored
      ? JSON.parse(stored)
      : undefined;
    const menus = siderMenus?.menus;

    // not loaded yet, or loaded but empty: fail open, the server still refuses
    if (!menus || menus.length === 0) {
      return <Component {...props} />;
    }

    const current = trimSlash(pathname);
    const allowed = menus
      .flatMap((menu) => menu.subMenus || [])
      .some((subMenu) => {
        const held = trimSlash(subMenu.path || '');
        if (!held) return false;
        // Equal, or a parent of the current path. The prefix case is required for the routes that carry
        // a parameter - /directory-download-config/form/:configName is reached from the
        // /directory-download-config menu - and the trailing slash keeps it segment safe, so a menu for
        // /directory cannot open /directory-management.
        return current === held || current.startsWith(`${held}/`);
      });

    // Home rather than /error/access, which withCheckAccessTime uses for the operating-hours case: this
    // user did not fail a check on a screen they were offered, they typed a URL that was never theirs.
    // Redirect replaces the history entry, so Back does not drop them straight onto it again.
    return allowed ? <Component {...props} /> : <Redirect to="/" />;
  };
}
